import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Field, ErrorMessage } from 'formik'
import { DropdownButton, FormControl, InputGroup } from 'react-bootstrap'
import {
  F_EMAIL_GATE,
  F_PWD_PROTECTION,
  F_PRESENTATION_MODE,
  F_MINIMALIST_MODE,
  F_EMAIL_WHITELIST
} from 'constants/plans/constants'
import SiteOption from './foundations/SiteOption'
import { hasFeature, isWasmerFile, onUpgradePlan } from 'utils/general'
import {
  DISPLAY_MODE_PRESENTATION,
  DISPLAY_MODE_STANDARD,
  GATED_MODE_EMAIL_GATE,
  GATED_MODE_PASSWORD,
  GATED_MODE_STANDARD,
  GATED_MODE_EMAIL_WHITELIST,
  DISPLAY_MODE_MINIMALIST,
  MODES,
  PDF_FILE,
  ZIP_FILE,
  GATED_MODE_SSO
} from 'components/CreateUpdateSiteModal/interface'
import SelectItem from './foundations/SelectItem'
import EmailTagInput, { emailTagInputStyles } from './foundations/EmailTagInput'
import { UPGRADE_MINIMALIST_PAYLOAD } from 'actions'
import { PLANS } from 'constants/plans'
import { MAX_DIRECT_UPLOAD_SIZE } from 'services/create-update-site/pro'
import {
  UPGRADE_FEEDBACK_PAYLOAD,
  UPGRADE_CAPTURE_EMAILS_PAYLOAD,
  UPGRADE_PASSWORD_PROTECT_PAYLOAD,
  UPGRADE_PRESENTATION_PAYLOAD,
  UPGRADE_EMAIL_WHITELIST_PAYLOAD
} from 'actions'
import { showUpgradeCardModal } from 'actions'
import { ToolTip } from '../../../PricingGrid/components/PlanCard'
import useCheckIsMobileScreen from '../../../../hooks/checkMobileScreen'
import { F_FEEDBACK_MODE } from 'constants/plans/constants'
import useEnterpriseSso from 'hooks/useEnterpriseSso'

const ToggleButtonsForm = ({ name, initialValues, userProfile, uploadValues, setFieldValue }) => {
  const dispatch = useDispatch()
  const { isMobile } = useCheckIsMobileScreen()
  const {
    displayMode,
    gatedMode,
    feedbackMode,
    passwordProtected,
    password,
    disableIndexing,
    disableExportPdf,
    siteConfiguration,
    errorPath,
    allowedEmails = []
  } = initialValues
  const { productId, childAccount } = userProfile
  const isChildAccount = !!childAccount
  const {
    entitled,
    connected,
    needsSetup,
    loading: ssoLoading,
    selectSsoMode
  } = useEnterpriseSso({
    enabled: !isChildAccount
  })
  const { selectedFile, addedFiles, fileType } = uploadValues

  useEffect(() => {
    if (siteConfiguration === true) {
      setFieldValue(`${name}.errorPath`, '/index.html')
    } else if (siteConfiguration === false) {
      setFieldValue(`${name}.errorPath`, '/')
    }
  }, [siteConfiguration])

  const onUpgrade = (planId, upgradePayload) => {
    dispatch(
      showUpgradeCardModal({
        ...upgradePayload,
        onClick: () => onUpgradePlan({ planId, userProfile }, dispatch)
      })
    )
  }

  const ssoSelectable = !ssoLoading && entitled && connected
  const ssoDescription = needsSetup ? 'Finish SSO setup first' : undefined

  return (
    <>
      {/** ACCESS GATE DROPDOWN */}
      {(selectedFile || fileType) && !isWasmerFile(selectedFile) && (
        <>
          <div className="align-items-center w-100 ps-md-3 pe-md-3 pb-1 pb-md-3">
            <div className="light-grey flex-grow-1">
              Access gate
              <ToolTip text="Choose how your content is restricted for visitors" />
            </div>
            <DropdownButton
              className={`d-grid ${isMobile ? 'bg-transparent' : 'bg-light-grey'} round-8 mt-3 ms-md-2 me-md-2 mb-0 text-start`}
              title={
                <span className="d-inline-flex" style={{ width: '95%' }}>
                  <img className="pt-1 me-2 op-6" src={`/assets/icons/${MODES[gatedMode].icon}`} height={22} />
                  {gatedMode}
                </span>
              }
              onSelect={(value) => {
                setFieldValue(`${name}.gatedMode`, value)
                setFieldValue(`${name}.passwordProtected`, value === GATED_MODE_PASSWORD)
                setFieldValue(`${name}.enableSSO`, value === GATED_MODE_SSO)
              }}
              variant="outline"
            >
              <SelectItem label={GATED_MODE_STANDARD} />
              <SelectItem
                label={GATED_MODE_PASSWORD}
                trial={!hasFeature(productId, F_PWD_PROTECTION)}
                learnLink="https://www.youtube.com/watch?v=rod_2W3GAGQ"
                onUpgrade={() => onUpgrade(PLANS.PRO.id, UPGRADE_PASSWORD_PROTECT_PAYLOAD)}
              />
              <SelectItem
                label={GATED_MODE_EMAIL_GATE}
                trial={!hasFeature(productId, F_EMAIL_GATE)}
                learnLink="https://www.youtube.com/watch?v=tlaDPsCF4Wo"
                onUpgrade={() => onUpgrade(PLANS.PRO_U.id, UPGRADE_CAPTURE_EMAILS_PAYLOAD)}
              />
              <SelectItem
                label={GATED_MODE_EMAIL_WHITELIST}
                trial={!hasFeature(productId, F_EMAIL_WHITELIST)}
                onUpgrade={() => onUpgrade(PLANS.PRO_U.id, UPGRADE_EMAIL_WHITELIST_PAYLOAD)}
              />
              {!isChildAccount && (
                <SelectItem
                  label={GATED_MODE_SSO}
                  description={ssoDescription}
                  selectable={ssoSelectable}
                  onIntercept={selectSsoMode}
                />
              )}
            </DropdownButton>
          </div>

          {/** EMAIL WHITELIST */}
          {gatedMode === GATED_MODE_EMAIL_WHITELIST && (
            <div className={emailTagInputStyles.emailWhitelistWrapper}>
              <div className={emailTagInputStyles.emailWhitelistFormGroup}>
                <label htmlFor="emailWhitelistInput" className={emailTagInputStyles.emailWhitelistLabel}>
                  Emails to allow
                </label>
                <EmailTagInput
                  id="emailWhitelistInput"
                  emails={allowedEmails}
                  onChange={(emails) => setFieldValue(`${name}.allowedEmails`, emails)}
                />
                <ErrorMessage name={`${name}.allowedEmails`} component="div" className="validation-error text-center" />
              </div>
            </div>
          )}

          {/** END ACCESS GATE DROPDOWN */}

          {/* PASSWORD */}
          {passwordProtected && (
            <div className="ps-5 pe-5 pb-3">
              <InputGroup className="flex" size="sm">
                <InputGroup.Text>Password</InputGroup.Text>
                <Field
                  name={`${name}.password`}
                  value={password}
                  type="text"
                  as={FormControl}
                  placeholder="Password"
                  aria-label="Password"
                  aria-describedby="inputGroupPrepend"
                />
              </InputGroup>
              <ErrorMessage name={`${name}.password`} component="div" className="validation-error text-center" />
            </div>
          )}
          {/* END PASSWORD */}

          {fileType === PDF_FILE && addedFiles.length === 0 && selectedFile?.size <= MAX_DIRECT_UPLOAD_SIZE && (
            <div className="align-items-center w-100 ps-md-3 pe-md-3 pb-3">
              <div className="light-grey flex-grow-1">
                Display Mode
                <ToolTip text="Choose how your content is restricted for visitors" />
              </div>
              <DropdownButton
                className="d-grid bg-light-grey round-8 mt-3 ms-2 me-2 mb-0 text-start"
                title={
                  <span className="d-inline-flex" style={{ width: '95%' }}>
                    <img className="pt-1 me-2 op-6" src={`/assets/icons/${MODES[displayMode].icon}`} height={22} />
                    {MODES[displayMode].name}
                  </span>
                }
                onSelect={(value) => {
                  setFieldValue(`${name}.displayMode`, value)
                }}
                variant="outline"
              >
                <SelectItem label={DISPLAY_MODE_STANDARD} name={MODES[DISPLAY_MODE_STANDARD].name} />
                <SelectItem
                  label={DISPLAY_MODE_PRESENTATION}
                  name={MODES[DISPLAY_MODE_PRESENTATION].name}
                  trial={!hasFeature(productId, F_PRESENTATION_MODE)}
                  learnLink="https://www.youtube.com/watch?v=rod_2W3GAGQ"
                  onUpgrade={() => onUpgrade(PLANS.TINY.id, UPGRADE_PRESENTATION_PAYLOAD)}
                />
                <SelectItem
                  label={DISPLAY_MODE_MINIMALIST}
                  name={MODES[DISPLAY_MODE_MINIMALIST].name}
                  trial={!hasFeature(productId, F_MINIMALIST_MODE)}
                  learnLink="https://www.youtube.com/watch?v=tlaDPsCF4Wo"
                  onUpgrade={() => onUpgrade(PLANS.TINY.id, UPGRADE_MINIMALIST_PAYLOAD)}
                />
              </DropdownButton>
            </div>
          )}
        </>
      )}

      {(selectedFile || fileType) && !isWasmerFile(selectedFile) && (
        <div className="ps-md-3 pe-md-3 pb-3">
          <div className="light-grey flex align-items-center">Settings</div>
          <div className="p-2 align-content-center m-auto">
            {/* HIDE FROM SEARCH ENGINE TOGGLE */}
            <SiteOption
              icon="hide.svg"
              label="Hide from Search Engines"
              desc="Prevent link from being indexed by search engines"
              trial={!productId}
              onChange={() => setFieldValue(`${name}.disableIndexing`, !disableIndexing)}
              checked={disableIndexing}
              onUpgrade={() =>
                onUpgrade(PLANS.TINY.id, {
                  title: 'Upgrade to hide from search engines',
                  desc: 'Prevent your project from being indexed by search engines.',
                  cta: 'Upgrade now'
                })
              }
            />
            {/* END HIDE FROM SEARCH ENGINE TOGGLE */}

            {/* HIDE FROM FEEDBACK TOGGLE */}
            <SiteOption
              icon="comment.svg"
              label="Enable Comments"
              desc="Allow visitors to comment on your work"
              onChange={() => setFieldValue(`${name}.feedbackMode`, !feedbackMode)}
              checked={feedbackMode}
              onUpgrade={() => onUpgrade(PLANS.PRO_U.id, UPGRADE_FEEDBACK_PAYLOAD)}
            />

            {(fileType === PDF_FILE ||
              fileType === ZIP_FILE ||
              (selectedFile && addedFiles && addedFiles.length > 0)) && (
              <SiteOption
                icon="no-download.svg"
                label="Disable downloads"
                desc="Prevent visitors from downloading any files"
                trial={!productId}
                onChange={() => setFieldValue(`${name}.disableExportPdf`, !disableExportPdf)}
                checked={disableExportPdf}
                onUpgrade={() =>
                  onUpgrade(PLANS.PRO.id, {
                    title: 'Upgrade to disable downloads',
                    desc: 'Prevent visitors from downloading files from your project.',
                    cta: 'Upgrade now'
                  })
                }
              />
            )}

            {/* ERROR PATH TOGGLE */}
            {fileType === ZIP_FILE && (
              <>
                <SiteOption
                  icon="cross.svg"
                  label="Error configuration"
                  desc="Add a path to a custom 404 error page when files not found"
                  trial={!productId}
                  onChange={() => setFieldValue(`${name}.siteConfiguration`, !siteConfiguration)}
                  checked={siteConfiguration}
                  onUpgrade={() =>
                    onUpgrade(PLANS.PRO.id, {
                      title: 'Upgrade for custom error pages',
                      desc: 'Configure a custom 404 error page for your project.',
                      cta: 'Upgrade now'
                    })
                  }
                >
                  <>
                    <InputGroup size="sm" className="p-3">
                      <InputGroup.Text>Error Path</InputGroup.Text>
                      <Field
                        name={`${name}.errorPath`}
                        type="text"
                        as={FormControl}
                        placeholder="/index.html"
                        aria-label="error-path"
                        aria-describedby="inputGroupPrepend"
                        value={errorPath}
                      />
                    </InputGroup>
                    <ErrorMessage name={`${name}.errorPath`} component="div" className="validation-error" />
                  </>
                </SiteOption>
              </>
            )}
            {/* END ERROR PATH TOGGLE */}
          </div>
        </div>
      )}
    </>
  )
}

export default ToggleButtonsForm
